import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { DETACH_PROMPT, formatPhone, irreversiblePrompt, retainCopy } from '../../../lib/phoneLifecycle.js';

function Shell({ children, onCancel }) {
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="panel p-5 max-w-md w-full space-y-4">{children}</div>
    </div>
  );
}

const BTN = 'px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50';

/**
 * The modal chain for number actions: detach confirm, retain-or-delete question,
 * irreversible-delete warning, and the $1.00 retention fee confirmation.
 * @param {{ dialog: {kind: 'detach'|'ask'|'immediate'|'retain'|'permanent', number: Object}|null, busy: boolean, onClose: () => void, onGo: (kind: string) => void, onConfirm: (kind: string) => void }} props
 *   `ask` -> No => onGo('immediate') / Yes => onGo('retain'); `permanent` is the Pending Deletion "Delete Permanently" warning.
 * @returns {JSX.Element|null}
 */
export default function NumberDialogs({ dialog, busy, onClose, onGo, onConfirm }) {
  if (!dialog) return null;
  const { kind, number } = dialog;
  const pretty = formatPhone(number.number);

  if (kind === 'detach') {
    return (
      <Shell onCancel={onClose}>
        <div className="font-display font-bold text-lg">Detach from leads?</div>
        <p className="text-sm text-white/70">{DETACH_PROMPT}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={`${BTN} btn-ghost`}>Cancel</button>
          <button type="button" disabled={busy} onClick={() => onConfirm('detach')} className={`${BTN} bg-civix-purple text-white hover:bg-civix-purpleDeep`}>Detach</button>
        </div>
      </Shell>
    );
  }
  const copy = retainCopy(number);
  if (kind === 'ask') {
    return (
      <Shell onCancel={onClose}>
        <div className="font-display font-bold text-lg">{copy.askTitle}</div>
        <p className="text-sm text-white/60">{pretty}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className={`${BTN} btn-ghost`}>Cancel</button>
          <button type="button" onClick={() => onGo('immediate')} className={`${BTN} border border-rose-400/50 text-rose-300 hover:bg-rose-500/10`}>No, Delete Immediately</button>
          <button type="button" onClick={() => onGo('retain')} className={`${BTN} bg-civix-purple text-white hover:bg-civix-purpleDeep`}>{copy.yesLabel}</button>
        </div>
      </Shell>
    );
  }
  if (kind === 'immediate' || kind === 'permanent') {
    return (
      <Shell onCancel={onClose}>
        <div className="flex items-center gap-2 text-rose-300 font-display font-bold text-lg"><AlertTriangle className="w-5 h-5" /> Irreversible Action</div>
        <p className="text-sm text-white/70">{irreversiblePrompt(number.number)}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={`${BTN} btn-ghost`}>Cancel</button>
          <button type="button" disabled={busy} onClick={() => onConfirm(kind)} className={`${BTN} bg-rose-500/80 hover:bg-rose-500 text-white`}>Confirm Permanent Deletion</button>
        </div>
      </Shell>
    );
  }
  if (kind === 'extend') {
    return (
      <Shell onCancel={onClose}>
        <div className="flex items-center gap-2 font-display font-bold text-lg"><Clock className="w-5 h-5 text-civix-purpleSoft" /> Extend Retention</div>
        <p className="text-sm text-white/70">Your Telecom Wallet will be charged $1.00 to hold {pretty} for 30 more days.</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={`${BTN} btn-ghost`}>Cancel</button>
          <button type="button" disabled={busy} onClick={() => onConfirm('extend')} className={`${BTN} bg-civix-purple text-white hover:bg-civix-purpleDeep`}>Confirm &amp; Charge $1.00</button>
        </div>
      </Shell>
    );
  }
  return (
    <Shell onCancel={onClose}>
      <div className="flex items-center gap-2 font-display font-bold text-lg"><Clock className="w-5 h-5 text-civix-purpleSoft" /> {copy.title}</div>
      <p className="text-sm text-white/70">{copy.body}</p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className={`${BTN} btn-ghost`}>Cancel</button>
        <button type="button" disabled={busy} onClick={() => onConfirm('retain')} className={`${BTN} bg-civix-purple text-white hover:bg-civix-purpleDeep`}>{copy.confirmLabel}</button>
      </div>
    </Shell>
  );
}
